/**
 * Demo Quiz Page
 *
 * A self-contained demo that simulates a live quiz experience without
 * requiring a WebSocket connection or moderator. Auto-advances through
 * questions to give users a feel for the live quiz system.
 * Fully internationalized with translation support.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../../lib/seo';

// Simulated other players for leaderboard
const SIMULATED_PLAYERS = [
  { name: "QuizMaster", avatar: "🦊", baseScore: 850 },
  { name: "BrainStorm", avatar: "🐸", baseScore: 720 },
  { name: "SpeedyMind", avatar: "🦁", baseScore: 680 },
  { name: "TriviaKing", avatar: "🐼", baseScore: 590 },
  { name: "SmartCookie", avatar: "🐨", baseScore: 450 },
];

const BUTTON_COLORS = [
  'from-red-500 to-red-600',
  'from-blue-500 to-blue-600',
  'from-yellow-500 to-yellow-600',
  'from-green-500 to-green-600',
];

const BUTTON_SHAPES = ['▲', '◆', '●', '■'];

type GamePhase = 'intro' | 'nickname' | 'lobby' | 'countdown' | 'question' | 'feedback' | 'leaderboard' | 'final';

interface PlayerScore {
  name: string;
  avatar: string;
  score: number;
  streak: number;
  correctCount: number;
}

interface Question {
  question: string;
  options: string[];
  correctIndex: number;
  timeLimit: number;
}

export default function DemoQuizPage() {
  const { t } = useTranslation('landing');
  const [phase, setPhase] = useState<GamePhase>('intro');
  const [nickname, setNickname] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('🦄');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints] = useState(0);
  const [leaderboard, setLeaderboard] = useState<PlayerScore[]>([]);
  const [countdownValue, setCountdownValue] = useState(3);

  // Build questions from translations
  const DEMO_QUESTIONS: Question[] = useMemo(() => [
    {
      question: t('demo.quiz.questions.q1'),
      options: [t('demo.quiz.questions.q1a1'), t('demo.quiz.questions.q1a2'), t('demo.quiz.questions.q1a3'), t('demo.quiz.questions.q1a4')],
      correctIndex: 1,
      timeLimit: 15,
    },
    {
      question: t('demo.quiz.questions.q2'),
      options: [t('demo.quiz.questions.q2a1'), t('demo.quiz.questions.q2a2'), t('demo.quiz.questions.q2a3'), t('demo.quiz.questions.q2a4')],
      correctIndex: 2,
      timeLimit: 12,
    },
    {
      question: t('demo.quiz.questions.q3'),
      options: [t('demo.quiz.questions.q3a1'), t('demo.quiz.questions.q3a2'), t('demo.quiz.questions.q3a3'), t('demo.quiz.questions.q3a4')],
      correctIndex: 2,
      timeLimit: 15,
    },
    {
      question: t('demo.quiz.questions.q4'),
      options: [t('demo.quiz.questions.q4a1'), t('demo.quiz.questions.q4a2'), t('demo.quiz.questions.q4a3'), t('demo.quiz.questions.q4a4')],
      correctIndex: 2,
      timeLimit: 12,
    },
    {
      question: t('demo.quiz.questions.q5'),
      options: [t('demo.quiz.questions.q5a1'), t('demo.quiz.questions.q5a2'), t('demo.quiz.questions.q5a3'), t('demo.quiz.questions.q5a4')],
      correctIndex: 1,
      timeLimit: 10,
    },
    {
      question: t('demo.quiz.questions.q6'),
      options: [t('demo.quiz.questions.q6a1'), t('demo.quiz.questions.q6a2'), t('demo.quiz.questions.q6a3'), t('demo.quiz.questions.q6a4')],
      correctIndex: 1,
      timeLimit: 15,
    },
    {
      question: t('demo.quiz.questions.q7'),
      options: [t('demo.quiz.questions.q7a1'), t('demo.quiz.questions.q7a2'), t('demo.quiz.questions.q7a3'), t('demo.quiz.questions.q7a4')],
      correctIndex: 2,
      timeLimit: 10,
    },
    {
      question: t('demo.quiz.questions.q8'),
      options: [t('demo.quiz.questions.q8a1'), t('demo.quiz.questions.q8a2'), t('demo.quiz.questions.q8a3'), t('demo.quiz.questions.q8a4')],
      correctIndex: 1,
      timeLimit: 15,
    },
    {
      question: t('demo.quiz.questions.q9'),
      options: [t('demo.quiz.questions.q9a1'), t('demo.quiz.questions.q9a2'), t('demo.quiz.questions.q9a3'), t('demo.quiz.questions.q9a4')],
      correctIndex: 2,
      timeLimit: 15,
    },
    {
      question: t('demo.quiz.questions.q10'),
      options: [t('demo.quiz.questions.q10a1'), t('demo.quiz.questions.q10a2'), t('demo.quiz.questions.q10a3'), t('demo.quiz.questions.q10a4')],
      correctIndex: 2,
      timeLimit: 12,
    },
  ], [t]);

  useSEO({
    title: t('demo.quiz.metaTitle'),
    description: t('demo.quiz.metaDescription'),
  });

  const avatars = ['🦄', '🦊', '🐸', '🦁', '🐼', '🐨', '🦋', '🐙', '🦜', '🐯', '🐰', '🐻'];

  // Calculate score based on time remaining
  const calculatePoints = useCallback((timeRemaining: number, totalTime: number) => {
    const timeRatio = timeRemaining / totalTime;
    const basePoints = 500;
    const bonusPoints = Math.round(500 * timeRatio);
    return basePoints + bonusPoints;
  }, []);

  // Generate leaderboard with simulated players
  const generateLeaderboard = useCallback(() => {
    const playerScore: PlayerScore = {
      name: nickname || 'You',
      avatar: selectedAvatar,
      score,
      streak,
      correctCount,
    };

    // Simulate other players' progress
    const simulatedScores = SIMULATED_PLAYERS.map((player) => {
      const questionProgress = currentQuestion + 1;
      const randomCorrect = Math.floor(Math.random() * 2) + Math.floor(questionProgress * 0.6);
      const simulatedScore = Math.min(player.baseScore * questionProgress / 5, player.baseScore + (randomCorrect * 600));
      return {
        name: player.name,
        avatar: player.avatar,
        score: Math.round(simulatedScore + (Math.random() * 200 - 100)),
        streak: Math.floor(Math.random() * 4),
        correctCount: Math.min(randomCorrect, questionProgress),
      };
    });

    const allPlayers = [playerScore, ...simulatedScores];
    allPlayers.sort((a, b) => b.score - a.score);
    setLeaderboard(allPlayers);
  }, [nickname, selectedAvatar, score, streak, correctCount, currentQuestion]);

  // Timer effect for questions
  useEffect(() => {
    if (phase !== 'question' || timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [phase, timeLeft]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (phase === 'question' && timeLeft === 0 && selectedAnswer === null) {
      handleAnswer(-1); // No answer selected
    }
  }, [phase, timeLeft, selectedAnswer]);

  // Countdown effect
  useEffect(() => {
    if (phase !== 'countdown') return;

    if (countdownValue > 0) {
      const timer = setTimeout(() => {
        setCountdownValue(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      startQuestion();
    }
  }, [phase, countdownValue]);

  const startGame = () => {
    if (!nickname.trim()) return;
    setPhase('lobby');
    // Auto-start after showing lobby
    setTimeout(() => {
      setPhase('countdown');
      setCountdownValue(3);
    }, 2000);
  };

  const startQuestion = () => {
    setPhase('question');
    setSelectedAnswer(null);
    setTimeLeft(DEMO_QUESTIONS[currentQuestion].timeLimit);
  };

  const handleAnswer = (answerIndex: number) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(answerIndex);

    const question = DEMO_QUESTIONS[currentQuestion];
    const isCorrect = answerIndex === question.correctIndex;

    if (isCorrect) {
      const points = calculatePoints(timeLeft, question.timeLimit);
      const streakBonus = streak >= 2 ? 1.2 : 1;
      const totalPoints = Math.round(points * streakBonus);

      setLastPoints(totalPoints);
      setScore(prev => prev + totalPoints);
      setStreak(prev => prev + 1);
      setCorrectCount(prev => prev + 1);
      setShowScorePopup(true);

      setTimeout(() => setShowScorePopup(false), 1500);
    } else {
      setStreak(0);
    }

    // Show feedback after a short delay
    setTimeout(() => {
      setPhase('feedback');
    }, 500);
  };

  const showLeaderboardPhase = () => {
    generateLeaderboard();
    setPhase('leaderboard');
  };

  const nextQuestion = () => {
    if (currentQuestion < DEMO_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setPhase('countdown');
      setCountdownValue(3);
    } else {
      generateLeaderboard();
      setPhase('final');
    }
  };

  const restartDemo = () => {
    setPhase('intro');
    setCurrentQuestion(0);
    setScore(0);
    setStreak(0);
    setCorrectCount(0);
    setSelectedAnswer(null);
    setNickname('');
  };

  // Render different phases
  const renderIntro = () => (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-8">
          <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">🎯</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('demo.quiz.intro.title')}</h1>
          <p className="text-xl text-white/80">
            {t('demo.quiz.intro.subtitle')}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('demo.quiz.intro.whatToExpect')}</h2>
          <ul className="text-left text-white/90 space-y-3">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">10</span>
              <span>{t('demo.quiz.intro.triviaQuestions')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">⏱️</span>
              <span>{t('demo.quiz.intro.timedAnswers')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">🔥</span>
              <span>{t('demo.quiz.intro.streakBonuses')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">🏆</span>
              <span>{t('demo.quiz.intro.liveLeaderboard')}</span>
            </li>
          </ul>
        </div>

        <button
          onClick={() => setPhase('nickname')}
          className="w-full py-4 bg-white text-indigo-600 rounded-xl font-bold text-lg hover:bg-indigo-50 transition-colors shadow-lg"
        >
          {t('demo.quiz.intro.startDemo')}
        </button>

        <Link
          to="/"
          className="block mt-4 text-white/70 hover:text-white transition-colors"
        >
          ← {t('demo.quiz.final.backToHome')}
        </Link>
      </div>
    </div>
  );

  const renderNickname = () => (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white text-center mb-6">{t('demo.quiz.nickname.title')}</h2>

          <div className="grid grid-cols-6 gap-2 mb-6">
            {avatars.map((avatar) => (
              <button
                key={avatar}
                onClick={() => setSelectedAvatar(avatar)}
                className={`w-12 h-12 rounded-xl text-2xl flex items-center justify-center transition-all ${
                  selectedAvatar === avatar
                    ? 'bg-white scale-110 shadow-lg'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {avatar}
              </button>
            ))}
          </div>

          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value.slice(0, 20))}
            placeholder={t('demo.quiz.nickname.placeholder')}
            className="w-full px-4 py-3 rounded-xl text-lg text-center font-medium bg-white/90 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-white/50 mb-6"
            maxLength={20}
            autoFocus
          />

          <button
            onClick={startGame}
            disabled={!nickname.trim()}
            className="w-full py-4 bg-green-500 text-white rounded-xl font-bold text-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('demo.quiz.nickname.joinGame')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderLobby = () => (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">{selectedAvatar}</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('demo.quiz.lobby.welcome', { name: nickname })}</h2>
          <p className="text-white/70 mb-6">{t('demo.quiz.lobby.getReady')}</p>

          <div className="flex justify-center gap-2 mb-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="w-3 h-3 bg-white rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>

          <div className="text-white/60 text-sm">
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              {t('demo.quiz.lobby.playersConnected', { count: 6 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCountdown = () => (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-white/80 text-xl mb-4">{t('demo.quiz.countdown.questionOf', { current: currentQuestion + 1, total: DEMO_QUESTIONS.length })}</p>
        <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto animate-pulse">
          <span className="text-7xl font-bold text-white">
            {countdownValue || '🚀'}
          </span>
        </div>
        <p className="text-white text-2xl font-bold mt-6">
          {countdownValue > 0 ? t('demo.quiz.countdown.getReady') : t('demo.quiz.countdown.go')}
        </p>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = DEMO_QUESTIONS[currentQuestion];
    const progress = (timeLeft / question.timeLimit) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex flex-col">
        {/* Score popup */}
        {showScorePopup && (
          <div className="fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 animate-bounce">
            <div className="bg-green-500 text-white px-6 py-3 rounded-full font-bold text-2xl shadow-2xl">
              +{lastPoints} {streak >= 3 && '🔥'}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-black/20 backdrop-blur-sm p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{selectedAvatar}</span>
              <div>
                <p className="text-white font-medium">{nickname}</p>
                <p className="text-white/70 text-sm">{score} {t('demo.quiz.question.points')} {streak >= 3 && <span>🔥{streak}</span>}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-sm">Question</p>
              <p className="text-white font-bold">{currentQuestion + 1}/{DEMO_QUESTIONS.length}</p>
            </div>
          </div>
        </div>

        {/* Timer bar */}
        <div className="h-2 bg-black/20">
          <div
            className={`h-full transition-all duration-1000 ${
              timeLeft <= 5 ? 'bg-red-500' : timeLeft <= 10 ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Question */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 text-center">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold ${
                  timeLeft <= 5 ? 'bg-red-500 animate-pulse' : 'bg-white/20'
                } text-white`}>
                  {timeLeft}
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white">{question.question}</h2>
            </div>

            {/* Answer options */}
            <div className="grid grid-cols-2 gap-4">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={selectedAnswer !== null}
                  className={`p-6 rounded-xl font-bold text-white text-lg transition-all transform hover:scale-105 active:scale-95 disabled:transform-none bg-gradient-to-br ${BUTTON_COLORS[index]} ${
                    selectedAnswer === index ? 'ring-4 ring-white' : ''
                  }`}
                >
                  <span className="text-2xl mr-3 opacity-50">{BUTTON_SHAPES[index]}</span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedback = () => {
    const question = DEMO_QUESTIONS[currentQuestion];
    const isCorrect = selectedAnswer === question.correctIndex;

    return (
      <div className={`min-h-screen flex flex-col items-center justify-center p-4 ${
        isCorrect ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-red-500 to-rose-600'
      }`}>
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-6xl">{isCorrect ? '✅' : '❌'}</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {isCorrect ? t('demo.quiz.feedback.correct') : t('demo.quiz.feedback.wrong')}
            </h2>
            {isCorrect && (
              <p className="text-white/90 text-xl">
                {t('demo.quiz.feedback.points', { points: lastPoints })} {streak >= 3 && <span>{t('demo.quiz.feedback.streakBonus', { streak })}</span>}
              </p>
            )}
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
            <p className="text-white/70 text-sm mb-2">{t('demo.quiz.feedback.correctAnswer')}</p>
            <p className="text-white text-xl font-bold">{question.options[question.correctIndex]}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-8">
            <div className="flex justify-between text-white">
              <span>{t('demo.quiz.feedback.yourScore')}</span>
              <span className="font-bold">{score} {t('demo.quiz.question.points')}</span>
            </div>
            <div className="flex justify-between text-white/70 mt-2">
              <span>{t('demo.quiz.feedback.correctAnswers')}</span>
              <span>{correctCount}/{currentQuestion + 1}</span>
            </div>
          </div>

          <button
            onClick={showLeaderboardPhase}
            className="w-full py-4 bg-white text-gray-900 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors"
          >
            {t('demo.quiz.feedback.seeLeaderboard')}
          </button>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    const playerRank = leaderboard.findIndex(p => p.name === nickname) + 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full">
          <h2 className="text-2xl font-bold text-white text-center mb-6">
            🏆 {t('demo.quiz.leaderboard.title')}
          </h2>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden mb-6">
            {leaderboard.slice(0, 5).map((player, index) => (
              <div
                key={player.name}
                className={`flex items-center gap-4 p-4 ${
                  player.name === nickname ? 'bg-white/20' : ''
                } ${index < leaderboard.length - 1 ? 'border-b border-white/10' : ''}`}
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  {index === 0 && <span className="text-2xl">🥇</span>}
                  {index === 1 && <span className="text-2xl">🥈</span>}
                  {index === 2 && <span className="text-2xl">🥉</span>}
                  {index > 2 && <span className="text-white/70 font-bold">{index + 1}</span>}
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl">
                  {player.avatar}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${player.name === nickname ? 'text-yellow-300' : 'text-white'}`}>
                    {player.name} {player.name === nickname && t('demo.quiz.leaderboard.you')}
                  </p>
                  <p className="text-white/60 text-sm">
                    {player.correctCount} {t('demo.quiz.leaderboard.correct')} {player.streak >= 3 && `🔥${player.streak}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{player.score}</p>
                  <p className="text-white/60 text-xs">{t('demo.quiz.question.points')}</p>
                </div>
              </div>
            ))}
          </div>

          {playerRank > 5 && (
            <div className="bg-yellow-500/20 backdrop-blur-sm rounded-xl p-4 mb-6 text-center">
              <p className="text-white">Your rank: <span className="font-bold">#{playerRank}</span></p>
            </div>
          )}

          <button
            onClick={nextQuestion}
            className="w-full py-4 bg-white text-indigo-600 rounded-xl font-bold text-lg hover:bg-indigo-50 transition-colors"
          >
            {currentQuestion < DEMO_QUESTIONS.length - 1 ? t('demo.quiz.leaderboard.nextQuestion') : t('demo.quiz.leaderboard.seeFinalResults')}
          </button>
        </div>
      </div>
    );
  };

  const renderFinal = () => {
    const playerRank = leaderboard.findIndex(p => p.name === nickname) + 1;
    const isWinner = playerRank === 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <div className="w-28 h-28 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <span className="text-6xl">
                {isWinner ? '🏆' : playerRank <= 3 ? '🎉' : '👏'}
              </span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {isWinner ? t('demo.quiz.final.youWon') : t('demo.quiz.final.gameOver')}
            </h1>
            <p className="text-white/80 text-xl">
              {t('demo.quiz.final.placement', { rank: playerRank, total: leaderboard.length })}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-white">{score}</p>
                <p className="text-white/60 text-sm">{t('demo.quiz.final.points')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{correctCount}</p>
                <p className="text-white/60 text-sm">{t('demo.quiz.final.correct')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{Math.round((correctCount / DEMO_QUESTIONS.length) * 100)}%</p>
                <p className="text-white/60 text-sm">{t('demo.quiz.final.accuracy')}</p>
              </div>
            </div>
          </div>

          {/* Top 3 podium */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
            <h3 className="text-white font-bold mb-4">{t('demo.quiz.final.finalStandings')}</h3>
            <div className="flex justify-center items-end gap-2 mb-4">
              {/* 2nd place */}
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 text-xl">
                  {leaderboard[1]?.avatar}
                </div>
                <div className="bg-gray-400 w-16 h-16 rounded-t-lg flex items-center justify-center">
                  <span className="text-2xl">🥈</span>
                </div>
                <p className="text-white/80 text-xs mt-1 truncate w-16">{leaderboard[1]?.name}</p>
              </div>
              {/* 1st place */}
              <div className="text-center">
                <div className="w-14 h-14 bg-yellow-400/30 rounded-full flex items-center justify-center mx-auto mb-2 text-2xl">
                  {leaderboard[0]?.avatar}
                </div>
                <div className="bg-yellow-500 w-20 h-24 rounded-t-lg flex items-center justify-center">
                  <span className="text-3xl">🥇</span>
                </div>
                <p className="text-white text-xs mt-1 font-bold truncate w-20">{leaderboard[0]?.name}</p>
              </div>
              {/* 3rd place */}
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 text-xl">
                  {leaderboard[2]?.avatar}
                </div>
                <div className="bg-orange-600 w-16 h-12 rounded-t-lg flex items-center justify-center">
                  <span className="text-2xl">🥉</span>
                </div>
                <p className="text-white/80 text-xs mt-1 truncate w-16">{leaderboard[2]?.name}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={restartDemo}
              className="w-full py-4 bg-white text-indigo-600 rounded-xl font-bold text-lg hover:bg-indigo-50 transition-colors"
            >
              {t('demo.quiz.final.playAgain')}
            </button>
            <Link
              to="/register"
              className="block w-full py-4 bg-green-500 text-white rounded-xl font-bold text-lg hover:bg-green-600 transition-colors"
            >
              {t('demo.quiz.final.createYourOwn')}
            </Link>
            <Link
              to="/"
              className="block text-white/70 hover:text-white transition-colors mt-4"
            >
              ← {t('demo.quiz.final.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // Render based on current phase
  switch (phase) {
    case 'intro':
      return renderIntro();
    case 'nickname':
      return renderNickname();
    case 'lobby':
      return renderLobby();
    case 'countdown':
      return renderCountdown();
    case 'question':
      return renderQuestion();
    case 'feedback':
      return renderFeedback();
    case 'leaderboard':
      return renderLeaderboard();
    case 'final':
      return renderFinal();
    default:
      return renderIntro();
  }
}
